import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {QuestionComponent} from '../question.component';
import {ImageManipulationService, IResizeImageOptions} from '../image-manipulation.service';
import {ImageAsset, ImageAssetFactory, ImageAssetType} from '../../domain/card';
import {NgxSpinnerService} from 'ngx-spinner';
import {OcrService, TextDetection} from '../ocr.service';
import {OcrActionRegex} from '../../domain/card-actions/ocr.action';
import {ScrollService} from '../scroll.service';

@Component({
  selector: `crds-question-ocr-image-upload`,
  template: `
    <div *ngIf="show" [formGroup]="form">
      <label *ngIf="question.label" class="form-label" [attr.for]="question.key">{{question.label}}:
        <span *ngIf="question.required"> *</span>
        <crds-form-error *ngIf="isInvalid" [question]="question"
                         [message]="question.errorMessage"></crds-form-error>
      </label>

      <!-- QUESTION INPUT -->
      <input class="imageCapture" type="file" style="display:none;" (change)="setFromOcr($event)" accept="image/*">
      <button (click)="clickFile()"
              type="button"
              class="btn btn-primary image-upload-button"
              #ocrImageUploadButton>
        Upload image<i class="material-icons button-icon">linked_camera</i>
      </button>

      <!-- QUESTION EXAMPLE IMAGE OR USER UPLOADED -->
      <div class="image-wrapper">
        <crds-question-hint [question]="question"></crds-question-hint>
        <div *ngIf="form.get(question.key).value === null">
          <crds-image-asset [images]="images" [key]="question.key"></crds-image-asset>
        </div>
        <div *ngIf="form.get(question.key).value !== null">
          <img [id]="question.key + '_temp'"
               [src]="form.get(question.key).value"/>
        </div>
      </div>

      <ngx-spinner
        bdColor="rgba(51, 51, 51, 0.8)"
        size="large"
        type="line-scale">
      </ngx-spinner>
    </div>`,
})
export class OcrImageUploadQuestionComponent extends QuestionComponent implements OnInit {

  @Input() form;
  @Input() question;
  @ViewChild('ocrImageUploadButton') input;
  type: string;
  images: ImageAsset[];
  imageAssetType = ImageAssetType;

  constructor(private spinnerService: NgxSpinnerService,
              private imageService: ImageManipulationService,
              private imageAssetFactory: ImageAssetFactory,
              private ocrService: OcrService,
              private scrollService: ScrollService) {
    super();
  }

  ngOnInit() {
    this.images = this.imageAssetFactory.create(this.question.image, this.form);
    this.scrollService.scrollObservable
      .subscribe((documentId) => {
        if (documentId === this.question.key) {
          this.input.nativeElement.scrollIntoView();
        }
      });
  }

  get isInvalid() {
    return this.form.get(this.question.key).touched && this.form.get(this.question.key).invalid;
  }

  get show() {
    const showIfQuestionPopulated = this.question.showIf ?
      this.showIf :
      true;
    return (this.question.showIfPopulated !== true || (this.value)) &&
      showIfQuestionPopulated && this.question.show !== false;
  }

  clickFile() {
    (document.getElementsByClassName('imageCapture')[0] as HTMLElement).click();
  }

  setFromOcr(event: any) {
    this.spinnerService.show();
    this.clearOcrTarget();
    const file: File = event.target.files[0];
    const targetFormControl = this.form.get(this.question.targetOcrField);
    let tempImage: any;
    let orientationTemp = 1;

    setTimeout(() => {
      this.spinnerService.hide();
    }, 10000);

    return this.imageService.resetOrientation(file)

      .then((reorientationResult: { result: string, orientation: number }) => {
        orientationTemp = reorientationResult.orientation;
        tempImage = reorientationResult.result;
        const resizeForOcrOptions: IResizeImageOptions = {
          maxSize: 2000,
          b64: tempImage
        };
        return this.imageService.resizeImage(resizeForOcrOptions);
      })
      .then((resizedImage) => {
        tempImage = resizedImage;
        return this.ocrService.recognise(tempImage);
      })
      .then((results: TextDetection[]) => {
        const filteredResult = this.filterResults(results);
        this.patchOcrTarget(filteredResult);
        if (filteredResult === null) {
          return Promise.resolve(tempImage);
        }
        return this.imageService.cropImage(tempImage, filteredResult);
      })
      .then((croppedImage: string) => {
        targetFormControl.patchValue(croppedImage);
        this.spinnerService.hide();
        this.resetFileInput();
      })
      .catch(console.error);
  }

  private filterResults(results: TextDetection[]): TextDetection | null {
    let returnedResult: TextDetection = null;

    this.question.regexs.forEach((regex: OcrActionRegex) => {

      switch (regex.type) {
        case 'standard':
          const standardFilteredResults = results.filter((result) => result.Type === 'WORD');
          const standardResult = this.standardFilter(standardFilteredResults, regex);
          if (standardResult) {
            returnedResult = standardResult;
          }
          break;
        case 'split':
          const splitFilteredResults = results.filter((result) => result.Type === 'LINE');
          const splitResult = this.splitFilter(splitFilteredResults, regex);
          if (splitResult) {
            returnedResult = splitResult;
          }
          break;
        default:
          returnedResult = null;
      }
    });

    return returnedResult;
  }

  /**
   * Attempt to find the index of the first pattern in the results then try to match the subsequent
   * results against the subsequent patterns.
   * @returns TextDetection | null
   * @param results
   * @param regex
   */
  private splitFilter(results: TextDetection[], regex: OcrActionRegex): TextDetection | null {
    let match: TextDetection = null;

    results.forEach((result) => {
      if (result.DetectedText.match(regex.patterns[0]) !== null) {
        match = result;
      }
    });

    return match;
  }

  private standardFilter(results: TextDetection[], regex: OcrActionRegex): TextDetection | null {
    const filteredResults = results.filter(
      (result) => result.DetectedText.match(regex.patterns[0]) !== null
    );
    return filteredResults.length ? filteredResults[0] : null;
  }

  private patchOcrTarget(result: TextDetection) {
    const target = this.form.get(this.question.targetOcr);
    if (result !== null) {
      const match = result.DetectedText.match(/(([1-2][0-9]|3[0-2]) ?[0-9]{4} ?[0-9]{4} ?[0-9]{3}).*/);
      target.patchValue(match.length ? match[1].replace(/ /g, '') : '');
    } else {
      target.patchValue('');
    }
  }

  private clearOcrTarget() {
    this.form.get(this.question.targetOcr).reset('');
  }

  resetFileInput() {
    const input = document.getElementById('ocr');
    input['value'] = '';

    if (!/safari/i.test(navigator.userAgent)) {
      input['type'] = '';
      input['type'] = 'file';
    }
  }
}
